import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

const VIDEOS = [
  {
    id: "routine",
    title: "Watch my wellness routine",
    videoId: "eL7QhZNU-OE",
    ctaLabel: "SHOP MY FAVORITES ❤️",
    ctaHref: "/shop",
    external: false,
  },
  {
    id: "coffee",
    title: "How to make the perfect protein coffee",
    videoId: "FRFZvOcXgu8",
    ctaLabel: "SHOP PROTEIN COFFEE ❤️",
    ctaHref: "https://shellyperreault.herbalife.com/en-us/u/search?q=Progein%20coffee&tab=Products",
    external: true,
  },
  {
    id: "flight",
    title: "Supplements must-have for flight attendant training",
    videoId: "5gS0cw2fa58",
    ctaLabel: "SHOP THE SUPPLEMENTS ❤️",
    ctaHref: "/shop",
    external: false,
  },
  {
    id: "donut-shot",
    title: "My Prolessa “Donut Shot” recipe",
    videoId: "WsS64SwBYwI",
    ctaLabel: "SHOP PROLESSA ❤️",
    ctaHref: "https://shellyperreault.herbalife.com/en-us/u/search?q=Prolessa&tab=Products",
    external: true,
  },
];

const CTA_CLASSES = {
  coral: "bg-coral shadow-lg shadow-coral/30 hover:bg-coraldark",
  forest: "bg-forest hover:bg-coral",
};

const VideoCarousel = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "center" });
  const [selected, setSelected] = useState(0);

  const onSelect = useCallback(() => {
    if (emblaApi) setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  return (
    <div data-testid="video-carousel">
      <p className="text-center font-display text-xs font-bold tracking-[0.3em] text-coral">MY VIDEOS — SWIPE THROUGH</p>

      <div className="relative mt-8">
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {VIDEOS.map((v, i) => (
              <div key={v.id} data-testid={`video-slide-${v.id}`} className="min-w-0 flex-[0_0_100%] px-1">
                <h3 className="text-center font-display text-2xl font-black uppercase tracking-tight text-ink md:text-3xl">
                  {v.title}
                </h3>
                <div data-testid={`video-frame-${v.id}`} className="mt-8 aspect-video w-full overflow-hidden rounded-3xl border border-forest/10 shadow-xl shadow-forest/10">
                  {selected === i || Math.abs(selected - i) === 1 ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${v.videoId}`}
                      title={v.title}
                      className="h-full w-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-forest/5" />
                  )}
                </div>
                <div className="mt-8 text-center">
                  {v.external ? (
                    <a
                      href={v.ctaHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`video-shop-button-${v.id}`}
                      className={`group inline-flex items-center gap-2 rounded-full px-9 py-4 font-display text-sm font-bold tracking-[0.15em] text-bone transition-all duration-300 hover:scale-105 ${i === 0 ? CTA_CLASSES.coral : CTA_CLASSES.forest}`}
                    >
                      {v.ctaLabel}
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </a>
                  ) : (
                    <Link
                      to={v.ctaHref}
                      data-testid={`video-shop-button-${v.id}`}
                      className={`group inline-flex items-center gap-2 rounded-full px-9 py-4 font-display text-sm font-bold tracking-[0.15em] text-bone transition-all duration-300 hover:scale-105 ${i === 0 ? CTA_CLASSES.coral : CTA_CLASSES.forest}`}
                    >
                      {v.ctaLabel}
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          data-testid="video-prev-button"
          onClick={() => emblaApi?.scrollPrev()}
          aria-label="Previous video"
          className="absolute left-2 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-forest/15 bg-bone/90 text-forest shadow-md backdrop-blur transition-all duration-300 hover:scale-110 hover:bg-coral hover:text-bone"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          data-testid="video-next-button"
          onClick={() => emblaApi?.scrollNext()}
          aria-label="Next video"
          className="absolute right-2 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-forest/15 bg-bone/90 text-forest shadow-md backdrop-blur transition-all duration-300 hover:scale-110 hover:bg-coral hover:text-bone"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      <div className="mt-8 flex justify-center gap-3">
        {VIDEOS.map((v, i) => (
          <button
            key={v.id}
            data-testid={`video-dot-${i}`}
            aria-label={`Go to video ${i + 1}`}
            onClick={() => emblaApi?.scrollTo(i)}
            className={`h-2.5 rounded-full transition-all duration-300 ${selected === i ? "w-8 bg-coral" : "w-2.5 bg-forest/25 hover:bg-forest/50"}`}
          />
        ))}
      </div>
    </div>
  );
};

export default VideoCarousel;
